import { IconButton, Tooltip } from '@mui/material'
import { useCallback, useState } from 'react'
import { IoCreateOutline } from 'react-icons/io5'

import { useSuccess } from '@/hooks'
import { useLazyGetQuestionContentQuery, useUpdateQuestionMutation } from '@/services/api'
import type { Question, QuestionUpdatePayload } from '@/types'

import QuestionEditForm from './QuestionEditForm'

interface QuestionEditProps {
  isMobile: boolean
  question: Question
}

function QuestionEdit({ question, isMobile }: QuestionEditProps) {
  const [triggerUpdate, { isLoading: isUpdating, isSuccess }] =
    useUpdateQuestionMutation()
  const [triggerGetDetails, { isLoadingDetails }] = useLazyGetQuestionContentQuery({
    selectFromResult: ({ isLoading, isFetching }) => ({
      isLoadingDetails: isLoading || isFetching,
    }),
  })
  const [questionData, setQuestionData] = useState<
    (Question & { description: string }) | null
  >(null)
  const [formOpen, setFormOpen] = useState<boolean>(false)
  const [fetchError, setFetchError] = useState<string>('')

  const handleOpenForm = useCallback(async () => {
    setFormOpen(true)
    setQuestionData(null)
    setFetchError('')

    try {
      const fullQuestion = await triggerGetDetails(question.id).unwrap()

      setQuestionData({
        ...question,
        description: fullQuestion.description,
      })
    } catch (error) {
      setFetchError(`Erro ao buscar os detalhes da questão: ${error}`)
    }
  }, [question, triggerGetDetails])

  const handleSubmit = useCallback(
    async (data: QuestionUpdatePayload) => {
      await triggerUpdate(data).unwrap()
      setFormOpen(false)
      setQuestionData(null)
    },
    [triggerUpdate],
  )

  const handleClose = useCallback(() => {
    setFormOpen(false)
    setQuestionData(null)
    setFetchError('')
  }, [])

  const handleRetry = useCallback(async () => {
    setFetchError('')

    try {
      const fullQuestion = await triggerGetDetails(question.id).unwrap()

      setQuestionData({
        ...question,
        description: fullQuestion.description,
      })
    } catch (error) {
      setFetchError(`
        Erro na nova tentativa de buscar os detalhes da questão: ${error}
      `)
    }
  }, [question, triggerGetDetails])

  useSuccess(isSuccess, 'A questão foi atualizada com sucesso!')

  return (
    <>
      <Tooltip title="Editar questão" arrow placement={isMobile ? 'bottom' : 'top'}>
        <IconButton
          onClick={handleOpenForm}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            width: isMobile ? 32 : 40,
            height: isMobile ? 32 : 40,
            backgroundColor: ({ palette }) => palette.primary.main + '15',
            color: ({ palette }) => palette.primary.main,
            border: ({ palette }) => `1px solid ${palette.primary.main}20`,
            boxShadow: ({ shadows }) => shadows[1],
            '&:hover': {
              backgroundColor: ({ palette }) => palette.primary.main + '25',
              boxShadow: ({ shadows }) => shadows[3],
              transform: 'translateY(-1px)',
              border: ({ palette }) => `1px solid ${palette.primary.main}40`,
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: ({ shadows }) => shadows[1],
            },
            transition: ({ transitions }) =>
              transitions.create(
                ['background-color', 'box-shadow', 'transform', 'border'],
                {
                  duration: transitions.duration.short,
                },
              ),
          }}>
          <IoCreateOutline size={isMobile ? 16 : 18} />
        </IconButton>
      </Tooltip>

      <QuestionEditForm
        open={formOpen}
        question={questionData}
        onSubmit={handleSubmit}
        onClose={handleClose}
        isLoading={isUpdating}
        isLoadingDetails={isLoadingDetails}
        fetchError={fetchError}
        onRetry={handleRetry}
      />
    </>
  )
}

export default QuestionEdit
